/**
 * advert init pipeline — оркестрация загрузки рекламы (ADF)
 * разбито по слоям: таймер скрытия, SSP-ответ, инициализация Adf, retry
 */

import { log } from '@shared/lib/helpers/debugLog'

const HIDE_TIMEOUT_MS = 10000
const ADF_RETRY_DELAY_MS = 500
const ADF_MAX_RETRIES = 6

export type AdvertInitParams = {
  setShowAdv: (v: boolean) => void
  onEmptyAdv?: () => void
  onLoadedAdv?: () => void
  setHasFirstSuccessRequest: (v: boolean) => void
  isDev: boolean
  nodeId: string
  options_puid: Record<string, unknown>
  options_ssp: Record<string, unknown>
}

const createHideTimer = (params: AdvertInitParams) =>
  setTimeout(() => {
    params.setShowAdv(false)
    params.onEmptyAdv && params.onEmptyAdv()
  }, HIDE_TIMEOUT_MS)

const createSspResponseHandler = (params: AdvertInitParams) => (response: { canHide?: boolean }) => {
  const onCanHide = () => {
    !params.isDev && params.setShowAdv(false)
    params.onEmptyAdv && params.onEmptyAdv()
    console.warn(response)
  }

  const onSuccess = () => {
    params.onLoadedAdv && params.onLoadedAdv()
    params.setShowAdv(true)
  }

  const handler = response.canHide ? onCanHide : onSuccess

  handler()
}

const createInitCallback = (params: AdvertInitParams, timerToHideShowAdv: ReturnType<typeof setTimeout>) => () => {
  clearTimeout(timerToHideShowAdv)

  const method = 'begun-scroll-rate' in params.options_ssp ? 'sspScroll' : 'ssp'
  window.Adf.banner[method](params.nodeId, params.options_puid, params.options_ssp)
    .then(createSspResponseHandler(params))
    .catch((e) => {
      console.error(e)
      params.onEmptyAdv && params.onEmptyAdv()
      params.setShowAdv(false)
    })
    .finally(() => {
      params.setHasFirstSuccessRequest(true)
    })
}

const runOrQueueAdf = (initCallback: () => void) => {
  const queueInit = () => {
    window.begun_new_callbacks = window.begun_new_callbacks || []
    window.begun_new_callbacks.push({ lib: { init: initCallback } })
  }
  const runInit = () => initCallback()

  const tryRun = (retriesLeft: number) => {
    const adfReady = typeof window.Adf !== 'undefined'
    const exhausted = retriesLeft <= 0
    adfReady && runInit()
    !adfReady && exhausted && queueInit()
    !adfReady && !exhausted && setTimeout(() => tryRun(retriesLeft - 1), ADF_RETRY_DELAY_MS)
  }

  tryRun(ADF_MAX_RETRIES)
}

export const runAdvertInitPipeline = (params: AdvertInitParams) => {
  log('runAdvertInitPipeline. params - ', params)
  const timerToHideShowAdv = createHideTimer(params)
  const initCallback = createInitCallback(params, timerToHideShowAdv)
  runOrQueueAdf(initCallback)
}
